import * as React from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Tooltip } from '@/components/ui/tooltip'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode
  /** When supplied, a clear affordance appears as soon as the field is non-empty. */
  onClear?: () => void
}

/**
 * The bordered box is the wrapper, not the `<input>` — that keeps the icon, the
 * field and the clear button on one baseline and lets `className` size the whole
 * control (`className="w-72"` does what it looks like it does).
 *
 * The field is 16px on phones and 14.5px from `md` up. That is not a taste
 * call: iOS Safari zooms the whole page when a focused input's text is smaller
 * than 16px, and a search box that zooms the table out of frame every time you
 * tap it is unusable. The control is also a notch taller below `md` so it is a
 * comfortable thumb target.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { icon, onClear, className, disabled, onChange, ...rest },
  ref
) {
  const isControlled = rest.value !== undefined
  const [uncontrolledFilled, setUncontrolledFilled] = React.useState(
    () => String(rest.defaultValue ?? '').length > 0
  )

  const filled = isControlled ? String(rest.value ?? '').length > 0 : uncontrolledFilled

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setUncontrolledFilled(e.target.value.length > 0)
      onChange?.(e)
    },
    [isControlled, onChange]
  )

  const handleClear = React.useCallback(() => {
    if (!isControlled) setUncontrolledFilled(false)
    onClear?.()
  }, [isControlled, onClear])

  return (
    <div
      data-disabled={disabled ? '' : undefined}
      className={cn(
        'group flex h-10 w-full items-center gap-2 rounded-md border border-line bg-s1 px-3 md:h-9',
        'transition-colors duration-100',
        'hover:border-line-strong',
        'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25 focus-within:hover:border-accent',
        'data-disabled:cursor-not-allowed data-disabled:bg-s3 data-disabled:opacity-60 data-disabled:hover:border-line',
        className
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className="flex flex-none items-center text-faint group-focus-within:text-muted [&_svg]:size-4 [&_svg]:shrink-0"
        >
          {icon}
        </span>
      ) : null}

      <input
        ref={ref}
        disabled={disabled}
        onChange={handleChange}
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent text-base text-fg outline-none md:text-[14.5px]',
          'placeholder:text-faint',
          'disabled:cursor-not-allowed'
        )}
        {...rest}
      />

      {onClear && filled && !disabled ? (
        <Tooltip content="Clear" side="top">
          <button
            type="button"
            aria-label="Clear input"
            onClick={handleClear}
            className={cn(
              'relative flex size-6 flex-none items-center justify-center rounded-sm text-faint md:size-5',
              'transition-colors duration-100 hover:bg-s3 hover:text-fg',
              "[@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:top-1/2",
              "[@media(pointer:coarse)]:before:left-1/2 [@media(pointer:coarse)]:before:-translate-x-1/2",
              "[@media(pointer:coarse)]:before:-translate-y-1/2",
              "[@media(pointer:coarse)]:before:size-11 [@media(pointer:coarse)]:before:content-['']"
            )}
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        </Tooltip>
      ) : null}
    </div>
  )
})
